/**
 * Constraint-rule AST — object model.
 *
 * A "rule" is the *intensional* definition of a whole class of constraints —
 * the symbolic math, quantified over the data tables — as opposed to the
 * *extensional* list of grounded constraints that {@link Model} holds today.
 * For example
 *
 *   def soc_dynamics(b: battery, t: timestep):
 *     SOC(b, t + 1) == SOC(b, t) + (sqrt(b.efficiency) * charge(b, t) - discharge(b, t) / sqrt(b.efficiency))
 *
 * grounds, against the concrete `battery` and `timestep` tables, into one
 * constraint per (battery, timestep) pair.
 *
 * ## Variables range over rows; columns are read with `.attr`
 *
 * Each quantifier is a parameter `<var>: <relation>` and binds a *row variable*. Two
 * coercions, both defined by the {@link RelationSchema} of the relation:
 *   - a bare row variable ({@link Ref}, e.g. `b`) → the relation's key. Used to
 *     index applied references, as in `SOC(b, t)`.
 *   - an applied reference ({@link Apply}, e.g. `discharge(n, t)`) → the
 *     relation's single non-key column (its value). Well-defined only when there
 *     is exactly one such column — the shape of every decision variable, and of
 *     single-value data tables like `demand`. Entity tables with several columns
 *     (`battery`) are never value-coerced; you reach their columns with
 *     {@link Attr} (`b.efficiency`).
 *
 * So the right-hand side only *references* what a quantifier put in scope, via
 * `.attr` reads and applied lookups; there are no free data accessors. Joins and
 * filters live in a quantifier's optional `if` predicate — `sum(flow(l, t) for
 * l in line if l.to == n)` is a join on the outer bus `n`.
 *
 * This module is just the AST plus a pretty-printer ({@link ruleToString}); the
 * schema-aware checks live in ./validate, and the evaluator that grounds a rule
 * against the data tables is a later step.
 */

// ---------------------------------------------------------------------------
// Expressions
// ---------------------------------------------------------------------------

/** A numeric literal, e.g. `1` or `0.9`. */
export interface NumLit {
  kind: 'num';
  value: number;
}

/**
 * A string literal, e.g. `"wind"`. Only meaningful where a value is compared or
 * used as a key — `g.fuel == "wind"` — never in arithmetic.
 */
export interface StrLit {
  kind: 'str';
  value: string;
}

/**
 * A reference to a bound row variable, e.g. `b`, `t`, `n`. Used as an index or
 * scalar it coerces to the row's key: a single-key relation unwraps to a scalar,
 * a composite key stays a tuple that spreads across the key positions it indexes.
 */
export interface Ref {
  kind: 'ref';
  name: string;
}

/** An attribute read off a bound row variable, e.g. `b.efficiency`, `l.from`. */
export interface Attr {
  kind: 'attr';
  obj: string;
  col: string;
}

/**
 * An applied reference to a relation, e.g. `discharge(n, t)`, `demand(n, t)`,
 * `theta(l.from, t)`. Coerces to the relation's single non-key column (its
 * value). Arguments are positional or keyword (matched against the relation's
 * key names).
 */
export interface Apply {
  kind: 'apply';
  relation: string;
  args: Arg[];
}

/** One argument of an {@link Apply}: positional (no name) or keyword (`bus=n`). */
export interface Arg {
  name?: string;
  value: Expr;
}

/** A built-in numeric function application, e.g. `sqrt(b.efficiency)`. */
export interface Call {
  kind: 'call';
  fn: string;
  args: Expr[];
}

/**
 * The built-in numeric functions, as evaluated by the interpreter. An applied
 * name outside this set is a relation reference ({@link Apply}), not a call —
 * which is how the parser tells `sqrt(x)` from `demand(n, t)`.
 */
export const BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(['sqrt', 'min', 'max', 'sin', 'cos']);

/** Negation, `-x`. */
export interface Neg {
  kind: 'neg';
  operand: Expr;
}

/** Binary arithmetic: `+`, `-`, `*`, `/`. */
export interface BinaryExpr {
  kind: 'binary';
  op: '+' | '-' | '*' | '/' | '^';
  left: Expr;
  right: Expr;
}

/**
 * Aggregation over the rows of a sub-query, written as a generator expression:
 * `sum(flow(l, t) for l in line if l.to == n)` — the summand first, then what it
 * ranges over. The variables bound by `over` are in scope inside `body`.
 */
export interface Sum {
  kind: 'sum';
  over: Quantifier[];
  body: Expr;
}

/**
 * A conditional, written Python's way round — value first, then the test:
 *
 *   min(g.max, wind_strength(t)) if g.fuel == "wind" else g.max
 *
 * `else` chains, so a whole case analysis is one expression, and with it a
 * relation defined by cases is one rule rather than one rule per case.
 *
 * The test is an ordinary {@link Pred}, the same thing a quantifier filters
 * with, so it reads the bound rows and nothing else — which means grounding can
 * settle which branch applies before the solve, and the chosen branch is the
 * only one that reaches the model.
 */
export interface Cond {
  kind: 'cond';
  when: Pred;
  then: Expr;
  otherwise: Expr;
}

export type Expr = NumLit | StrLit | Ref | Attr | Apply | Call | Neg | BinaryExpr | Sum | Cond;

// ---------------------------------------------------------------------------
// Quantifiers + predicates
// ---------------------------------------------------------------------------

/** `<var> in <relation>`, optionally filtered by a `where` predicate. */
export interface Quantifier {
  var: string;
  relation: string;
  where?: Pred;
}

export type CompareOp = '==' | '!=' | '<' | '<=' | '>' | '>=';

/** A comparison predicate, e.g. `l.to == n`. */
export interface Compare {
  kind: 'compare';
  op: CompareOp;
  left: Expr;
  right: Expr;
}

/** Conjunction of predicates, e.g. `d.bus == n and d.timestep == t`. */
export interface And {
  kind: 'and';
  left: Pred;
  right: Pred;
}

export type Pred = Compare | And;

// ---------------------------------------------------------------------------
// Constraint body + rule
// ---------------------------------------------------------------------------

export type CmpOp = '<=' | '>=' | '=';

/** A single constraint relation, `left <op> right`. */
export interface Cmp {
  kind: 'cmp';
  op: CmpOp;
  left: Expr;
  right: Expr;
}

/**
 * A two-sided (ranged) constraint, `lower <= expr <= upper`. Kept distinct from
 * {@link Cmp} because in the current model these are usually variable *bounds*
 * (`lowerBound`/`upperBound`), not constraint rows — grounding decides which.
 */
export interface Range {
  kind: 'range';
  lower: Expr;
  expr: Expr;
  upper: Expr;
}

/**
 * A rule whose body is a bare expression *defines* a data relation rather than
 * asserting anything: one row per binding of its parameters, holding that value.
 *
 *   def gen_cap(g: gen, t: timestep):
 *     min(g.max, wind_strength(t)) if g.fuel == "wind" else g.max
 *
 * One rule defines one relation. A value that varies by case says so with a
 * conditional ({@link Cond}) rather than by splitting into a rule per case.
 */
export interface Define {
  kind: 'define';
  expr: Expr;
}

/**
 * What a rule's body can be, and with it what the rule *is*: a comparison
 * grounds into constraint rows, a range declares a decision variable and its
 * bounds, and a bare expression defines a derived data relation.
 */
export type RuleBody = Cmp | Range | Define;

/** @deprecated Use {@link RuleBody} — kept while callers migrate. */
export type ConstraintExpr = RuleBody;

/** A class of constraints: quantifiers over the data tables plus a body relation. */
export interface Rule {
  /** Matches the constraint `group` in {@link Model}, e.g. "dcpf", "balance". */
  name: string;
  quantifiers: Quantifier[];
  body: RuleBody;
}

/**
 * The objective: minimize or maximize a linear expression, e.g.
 * `minimize cost = sum(g in gen, t in timestep: g.cost * gen_output(g, t)) + …`.
 * The expression uses the same sums/arithmetic as a rule body; its ground
 * coefficient map is the objective.
 */
export interface Objective {
  sense: 'minimize' | 'maximize';
  name: string;
  expr: Expr;
}

// ---------------------------------------------------------------------------
// Constructors — terse helpers for authoring rules by hand (and, later, the
// natural output shape of a parser).
// ---------------------------------------------------------------------------

export const num = (value: number): NumLit => ({ kind: 'num', value });
export const str = (value: string): StrLit => ({ kind: 'str', value });
export const ref = (name: string): Ref => ({ kind: 'ref', name });
export const attr = (obj: string, col: string): Attr => ({ kind: 'attr', obj, col });
export const call = (fn: string, ...args: Expr[]): Call => ({ kind: 'call', fn, args });
export const neg = (operand: Expr): Neg => ({ kind: 'neg', operand });

/** A keyword argument, e.g. `kw('bus', ref('n'))` renders `bus=n`. */
export const kw = (name: string, value: Expr): Arg => ({ name, value });
const toArg = (a: Expr | Arg): Arg => ('kind' in a ? { value: a } : a);
/** An applied reference; bare {@link Expr} args are positional, {@link kw} args keyword. */
export const apply = (relation: string, ...args: (Expr | Arg)[]): Apply => ({
  kind: 'apply',
  relation,
  args: args.map(toArg),
});

export const add = (left: Expr, right: Expr): BinaryExpr => ({ kind: 'binary', op: '+', left, right });
export const sub = (left: Expr, right: Expr): BinaryExpr => ({ kind: 'binary', op: '-', left, right });
export const mul = (left: Expr, right: Expr): BinaryExpr => ({ kind: 'binary', op: '*', left, right });
export const div = (left: Expr, right: Expr): BinaryExpr => ({ kind: 'binary', op: '/', left, right });
export const pow = (left: Expr, right: Expr): BinaryExpr => ({ kind: 'binary', op: '^', left, right });

export const sum = (over: Quantifier[], body: Expr): Sum => ({ kind: 'sum', over, body });

/** `then if when else otherwise`. Nest in `otherwise` to chain further cases. */
export const cond = (when: Pred, then: Expr, otherwise: Expr): Cond => ({
  kind: 'cond',
  when,
  then,
  otherwise,
});

/** A quantifier `<var> in <relation>`, optionally with a `where` predicate. */
export const bind = (varName: string, relation: string, where?: Pred): Quantifier => ({
  var: varName,
  relation,
  where,
});

export const compare = (op: CompareOp, left: Expr, right: Expr): Compare => ({ kind: 'compare', op, left, right });
export const eq = (left: Expr, right: Expr): Compare => compare('==', left, right);
export const and = (left: Pred, right: Pred): And => ({ kind: 'and', left, right });

export const cmp = (op: CmpOp, left: Expr, right: Expr): Cmp => ({ kind: 'cmp', op, left, right });
export const range = (lower: Expr, expr: Expr, upper: Expr): Range => ({ kind: 'range', lower, expr, upper });
/** A definition body: the value each grounded row of a derived relation holds. */
export const define = (expr: Expr): Define => ({ kind: 'define', expr });

export const objective = (sense: Objective['sense'], name: string, expr: Expr): Objective => ({ sense, name, expr });

// ---------------------------------------------------------------------------
// Pretty-printer
// ---------------------------------------------------------------------------

/** Binding tightness, used to insert the minimum number of parentheses. */
const PREC = { cond: 1, add: 2, mul: 3, unary: 4, pow: 5, atom: 6 } as const;

function precOf(e: Expr): number {
  switch (e.kind) {
    case 'cond':
      return PREC.cond;
    case 'binary':
      if (e.op === '+' || e.op === '-') return PREC.add;
      if (e.op === '^') return PREC.pow;
      return PREC.mul;
    case 'neg':
      return PREC.unary;
    default:
      return PREC.atom;
  }
}

/** Show integers plainly and round non-integers to 3 decimals. */
function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(3)).toString();
}

function argToString(a: Arg): string {
  const s = exprToString(a.value);
  return a.name ? `${a.name}=${s}` : s;
}

/**
 * Render an expression, parenthesizing only where precedence demands it.
 * Exported because a cost term is an expression on its own — one summand of the
 * objective, shown and edited without a `minimize` head.
 */
export function exprToString(e: Expr, parentPrec = 0): string {
  const s = exprInner(e);
  return precOf(e) < parentPrec ? `(${s})` : s;
}

function exprInner(e: Expr): string {
  switch (e.kind) {
    case 'num':
      return formatNumber(e.value);
    case 'str':
      return JSON.stringify(e.value);
    case 'ref':
      return e.name;
    case 'attr':
      return `${e.obj}.${e.col}`;
    case 'apply':
      return `${e.relation}(${e.args.map(argToString).join(', ')})`;
    case 'call':
      return `${e.fn}(${e.args.map((a) => exprToString(a)).join(', ')})`;
    case 'neg':
      return `-${exprToString(e.operand, PREC.unary)}`;
    case 'sum':
      // A generator expression: the summand first, then what it ranges over —
      // `sum(P_line(l, t) for l in line if l.to == n)`. Self-contained inside
      // the parens, so it can't be misread as juxtaposed multiplication.
      return `sum(${exprToString(e.body)} ${e.over.map(quantToString).join(' ')})`;
    case 'cond':
      // Right-associative: the `else` branch takes a bare conditional, so a
      // chain of cases reads as a flat list rather than a nest of parens.
      return `${exprToString(e.then, PREC.add)} if ${predToString(e.when)} else ${exprToString(
        e.otherwise,
        PREC.cond,
      )}`;
    case 'binary': {
      const p = precOf(e);
      // Exponentiation is right-associative and binds tightest, and reads best
      // tight (`l.r^2`): parenthesize a same-precedence *left* operand instead of
      // the right, and print without surrounding spaces.
      if (e.op === '^') {
        return `${exprToString(e.left, PREC.pow + 1)}^${exprToString(e.right, PREC.pow)}`;
      }
      // Every other binary operator parses left-associatively, so a same-precedence
      // right subtree is only ever there because it was grouped — bump the right
      // operand's context and it prints with the parens that put it there
      // (a - (b - c), a / (b * c), a + (b - c)). Without that the tree and the
      // text would disagree: `a + (b - c)` would print as `a + b - c` and parse
      // back as `(a + b) - c`.
      return `${exprToString(e.left, p)} ${e.op} ${exprToString(e.right, p + 1)}`;
    }
  }
}

function predToString(p: Pred): string {
  if (p.kind === 'and') return `${predToString(p.left)} and ${predToString(p.right)}`;
  // A conditional operand is parenthesized: `if` inside a test would otherwise
  // run into the conditional's own `if`.
  return `${exprToString(p.left, PREC.add)} ${p.op} ${exprToString(p.right, PREC.add)}`;
}

/**
 * A quantifier inside a `sum`, as a comprehension clause: `for l in line`, or
 * `for l in line if l.to == n` when it's filtered.
 */
export function quantToString(q: Quantifier): string {
  const head = `for ${q.var} in ${q.relation}`;
  return q.where ? `${head} if ${predToString(q.where)}` : head;
}

/**
 * A quantifier in a rule's head, as a typed parameter: `l: line`, or
 * `n: bus if n.in_balance == 1` when it's filtered.
 */
export function paramToString(q: Quantifier): string {
  const head = `${q.var}: ${q.relation}`;
  return q.where ? `${head} if ${predToString(q.where)}` : head;
}

/**
 * A whole body that is a chain of cases, as an if/elif/else block:
 *
 *   if g.fuel == "wind":
 *     min(g.max, wind_strength(t))
 *   elif g.fuel == "solar":
 *     min(g.max, solar_strength(t))
 *   else:
 *     g.max
 *
 * The same {@link Cond} the ternary `x if p else y` builds — this is how it's
 * written when it *is* the body, with the tests down the left margin and the
 * values indented under them, rather than trailing off one long line. Nested
 * inside arithmetic it still prints as the ternary, the way Python's does.
 *
 * Exported because a cost category's body is an expression too.
 */
export function blockCondToString(e: Cond, indent = ''): string {
  const inner = `${indent}  `;
  const lines = [`if ${predToString(e.when)}:`, `${inner}${exprToString(e.then)}`];
  let rest: Expr = e.otherwise;
  while (rest.kind === 'cond') {
    lines.push(`${indent}elif ${predToString(rest.when)}:`, `${inner}${exprToString(rest.then)}`);
    rest = rest.otherwise;
  }
  lines.push(`${indent}else:`, `${inner}${exprToString(rest)}`);
  return lines.join('\n');
}

/** A definition body: the block form when it's a conditional, else the expression. */
function defineToString(e: Expr, indent: string): string {
  return e.kind === 'cond' ? blockCondToString(e, indent) : exprToString(e);
}

function bodyToString(body: RuleBody, indent = '  '): string {
  if (body.kind === 'define') return defineToString(body.expr, indent);
  if (body.kind === 'range') {
    return `${exprToString(body.lower)} <= ${exprToString(body.expr)} <= ${exprToString(body.upper)}`;
  }
  // `==` rather than `=`: the body is an assertion about the two sides, and in
  // this syntax `=` is only ever a keyword argument.
  const op = body.op === '=' ? '==' : body.op;
  return `${exprToString(body.left)} ${op} ${exprToString(body.right)}`;
}

/**
 * Render a rule as a function definition — its quantifiers are the parameters,
 * one per row variable, annotated with the relation it ranges over:
 *
 *   def dcpf(l: line, t: timestep):
 *     P_line(l, t) == (theta(l.from, t) - theta(l.to, t)) / l.reactance
 *
 * The reading is "for every (line, timestep), this holds". The name is part of
 * the definition, not a label attached to it: it's the group the grounded rows
 * carry, and renaming is an ordinary edit.
 */
export function ruleToString(rule: Rule): string {
  const params = rule.quantifiers.map(paramToString).join(', ');
  return `def ${rule.name}(${params}):\n  ${bodyToString(rule.body)}`;
}

/** Render an objective, e.g. `minimize cost:\n  sum(...) + ...`. */
export function objectiveToString(obj: Objective): string {
  return `${obj.sense} ${obj.name}:\n  ${exprToString(obj.expr)}`;
}

/**
 * Render one cost category — a named summand of the objective, written as a
 * definition with no parameters, since it quantifies nothing on its own:
 *
 *   def generation():
 *     sum(g.cost * P_gen(g, t) for g in gen for t in timestep)
 */
export function costTermToString(term: { name: string; expr: Expr }): string {
  return `def ${term.name}():\n  ${defineToString(term.expr, '  ')}`;
}
